import styled from 'styled-components';

export const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  position: relative;
`;

export const InputError = styled.p`
  text-align: right;
  font-size: 0.88rem;
  color: #ff2e3e;
  position: absolute;
  top: -1.44rem;
  right: 5px;
`;

export const UploadArea = styled.div<{ $dragActive: boolean }>`
  border: 2px dashed
    ${({ $dragActive }) => ($dragActive ? '#ff9800' : '#e9e9e9')};
  border-radius: 0.44rem;
  padding: 1.1rem 1.2rem;
  text-align: center;
  cursor: pointer;
  background-color: ${({ $dragActive }) => ($dragActive ? '#f5fbff' : 'white')};
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff9800;
    background-color: rgba(255, 153, 0, 0.2);
  }
`;

export const UploadText = styled.div`
  margin-top: 0.89rem;
  color: #333333;
  font-size: 16px;
`;

export const FileTypes = styled.div`
  font-size: 0.66rem;
  color: #333333;
  margin-top: 0.4rem;
`;

export const PreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.89rem;
  margin-top: 1.3rem;
`;

export const PreviewItem = styled.div`
  position: relative;
  border-radius: 0.4rem;
  overflow: visible;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const ImagePreview = styled.img`
  width: 100%;
  height: 8rem;
  object-fit: cover;
`;

export const VideoPreview = styled.video`
  width: 100%;
  height: 8rem;
  background: black;
`;

export const FilePreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.1rem;
  background: #f5f5f5;
  height: 8rem;
`;

export const FileName = styled.div`
  font-size: 0.66rem;
  margin-top: 0.44rem;
  word-break: break-word;
  text-align: center;
`;

export const FileActions = styled.div`
  width: 100%;
  position: absolute;
  top: 50%; /* Deslocamento vertical em relação ao topo */
  left: 50%; /* Deslocamento horizontal em relação à esquerda */
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem;
  opacity: 1;
  transition: opacity 0.3s;
`;

export const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 0.2rem;
  cursor: pointer;
  color: white;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    color: #1e88e5;
  }
`;

export const TrashFileButton = styled.button`
  position: absolute;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  background: #e2e2e2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 0.2rem;
  cursor: pointer;
  color: white;

  top: -0.6rem;
  left: -0.6rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    color: #1e88e5;
  }
`;

export const ErrorMessage = styled.div`
  color: #ff4444;
  margin-top: 0.4rem;
  font-size: 0.78rem;
`;

/* ─── Moderation UI ──────────────────────────────────────────────────── */

const badgeColors = {
  loading: { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  approved: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  rejected: { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  error: { bg: '#fce4ec', color: '#b71c1c', border: '#ef9a9a' },
  // Neutro de propósito: formato ilegível não é um julgamento sobre o
  // conteúdo, e pintar de vermelho faz o usuário achar que foi reprovado.
  unsupported: { bg: '#eceff1', color: '#455a64', border: '#b0bec5' },
  // Vídeo publica na hora e é revisado depois — informativo, não um alerta.
  review: { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
};

export const ModerationStatusBadge = styled.span<{
  $variant:
    | 'loading'
    | 'approved'
    | 'rejected'
    | 'error'
    | 'unsupported'
    | 'review';
}>`
  position: absolute;
  bottom: 0.35rem;
  right: 0.35rem;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 0.3rem;
  z-index: 3;
  white-space: nowrap;
  pointer-events: none;
  line-height: 1.2;
  border: 1px solid ${({ $variant }) => badgeColors[$variant]?.border || '#ccc'};
  background-color: ${({ $variant }) => badgeColors[$variant]?.bg || '#f5f5f5'};
  color: ${({ $variant }) => badgeColors[$variant]?.color || '#333'};

  ${({ $variant }) =>
    $variant === 'loading' &&
    `
    animation: pulse 1.4s ease-in-out infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0.5; }
    }
  `}
`;

export const ModerationOverlay = styled.div<{
  $variant?: 'rejected' | 'unsupported';
}>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.5rem;
  background: ${({ $variant }) =>
    $variant === 'unsupported'
      ? 'rgba(55, 71, 79, 0.62)'
      : 'rgba(198, 40, 40, 0.55)'};
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  z-index: 2;
  pointer-events: none;
  border-radius: 0.4rem;
`;

/**
 * Erros por arquivo (formato ou tamanho) detectados antes do upload. Fica no
 * fluxo normal, abaixo da área de drop — o InputError é absoluto e comporta
 * só a mensagem única do unform.
 */
export const FileErrorList = styled.ul`
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  > li {
    font-size: 0.8rem;
    line-height: 1.35;
    color: #c62828;
  }
`;
